import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
  ArrowLeft, Users, Car, ClipboardList, CheckCircle, XCircle, 
  MapPin, Calendar, DollarSign, Eye, Download, MessageSquare,
  TrendingUp, Clock, Search, Filter, Star, Shield, Building2,
  Ban, Unlock, FileText, Image, CreditCard, ChevronRight, X, ExternalLink,
  Mail, Send, Loader2, Settings, LogOut, ChevronDown, Lock, User, Wallet, RefreshCw, Database
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { DocumentViewerModal } from '../components/admin/DocumentViewerModal';
import { AdminAnalytics } from '../components/admin/AdminAnalytics';
import { ProfileSettingsModal } from '../components/ProfileSettingsModal';
import { DriverDetailModal } from '../components/Admin/DriverDetailModal';
import { CompanyDetailModal } from '../components/Admin/CompanyDetailModal';
import { AdminTransfersTab } from '../components/admin/AdminTransfersTab';
import { TransferDetailModal } from '../components/admin/TransferDetailModal';
import { AdminPayoutsTab } from '../components/admin/AdminPayoutsTab';
import { useTranslation } from '../translations';
import { VehicleCategoryCMS } from '../components/admin/VehicleCategoryCMS';
import { AdminNotificationsTab } from '../components/admin/AdminNotificationsTab';
import { AdminReviewsTab } from '../components/admin/AdminReviewsTab';
import { UsersTab } from '../components/admin/UsersTab';
import { DriversTabContent } from '../components/admin/DriversTabContent';
import { AdminSettingsTab } from '../components/admin/AdminSettingsTab';
import { AdminSmsStatsTab } from '../components/admin/AdminSmsStatsTab';
import { AdminMaintenanceTab } from '../components/admin/AdminMaintenanceTab';

function EmailDiagnostics() {
  const [diag, setDiag] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const runDiagnostics = async () => {
    try {
      const res = await api.get('/admin/email-diagnostics');
      setDiag(res.data);
    } catch (e) {
      setDiag({ error: e.response?.data?.detail || 'Fehler' });
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/email-test', { to_email: testEmail });
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.detail || 'Fehler' });
    } finally {
      setTesting(false);
    }
  };

  const checkUser = async () => {
    if (!verifyEmail) return;
    setUserInfo(null);
    setVerifyResult(null);
    try {
      const res = await api.get(`/admin/user-verification-info?email=${encodeURIComponent(verifyEmail)}`);
      setUserInfo(res.data);
    } catch (e) {
      setVerifyResult({ success: false, message: e.response?.data?.detail || 'Benutzer nicht gefunden' });
    }
  };

  const manualVerify = async () => {
    if (!verifyEmail) return;
    setVerifyLoading(true);
    try {
      const res = await api.post('/admin/manual-verify-user', { email: verifyEmail });
      setVerifyResult({ success: true, message: res.data.message });
      setUserInfo(null);
    } catch (e) {
      setVerifyResult({ success: false, message: e.response?.data?.detail || 'Fehler' });
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Config Check */}
      <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="email-diagnostics">
        <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Shield size={18} className="text-blue-500" /> E-Mail Diagnose
        </h2>
        <div className="flex items-center gap-3 mb-4">
          <button onClick={runDiagnostics} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors" data-testid="run-diagnostics-btn">
            Konfiguration prüfen
          </button>
          {diag && !diag.error && (
            <div className="flex items-center gap-3 text-sm flex-wrap">
              <span className={`px-2 py-1 rounded-lg text-xs font-medium ${diag.status === 'OK' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                Methode: {diag.method}
              </span>
              {diag.smtp_configured && (
                <span className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700">
                  SMTP: {diag.smtp_user}
                </span>
              )}
              <span className="px-2 py-1 rounded-lg text-xs font-medium bg-slate-50 text-slate-700">
                Absender: {diag.sender_email}
              </span>
            </div>
          )}
          {diag?.error && <span className="text-sm text-red-600">{diag.error}</span>}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
            placeholder="Test-E-Mail-Adresse eingeben..."
            className="flex-1 border border-slate-200 rounded-lg px-4 py-2 text-sm"
            data-testid="test-email-input"
          />
          <button onClick={sendTestEmail} disabled={testing || !testEmail}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="send-test-email-btn"
          >
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Test senden
          </button>
        </div>
        {testResult && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid="test-email-result">
            {testResult.success ? '✓' : '✗'} {testResult.message}
          </div>
        )}
      </div>

      {/* Manual User Verification */}
      <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="manual-verify-section">
        <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
          <User size={18} className="text-emerald-500" /> Benutzer manuell verifizieren
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Falls die E-Mail-Zustellung nicht funktioniert, können Sie Benutzer hier manuell verifizieren oder den Code einsehen.
        </p>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="email" value={verifyEmail} onChange={e => setVerifyEmail(e.target.value)}
            placeholder="E-Mail-Adresse des Benutzers..."
            className="flex-1 border border-slate-200 rounded-lg px-4 py-2 text-sm"
            data-testid="verify-email-input"
          />
          <button onClick={checkUser} disabled={!verifyEmail}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 disabled:opacity-50 transition-colors"
            data-testid="check-user-btn"
          >
            Prüfen
          </button>
          <button onClick={manualVerify} disabled={verifyLoading || !verifyEmail}
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="manual-verify-btn"
          >
            {verifyLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Verifizieren
          </button>
        </div>
        {userInfo && (
          <div className="p-4 bg-slate-50 rounded-lg text-sm space-y-2" data-testid="user-info-display">
            <div className="flex items-center gap-3">
              <span className="text-slate-500 w-28">Name:</span>
              <span className="font-medium text-slate-800">{userInfo.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-500 w-28">E-Mail:</span>
              <span className="font-medium text-slate-800">{userInfo.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-500 w-28">Rolle:</span>
              <span className="font-medium text-slate-800">{userInfo.role}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-500 w-28">Verifiziert:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${userInfo.is_verified ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {userInfo.is_verified ? 'Ja' : 'Nein'}
              </span>
            </div>
            {!userInfo.is_verified && userInfo.has_verification_code && (
              <div className="flex items-center gap-3 mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="text-amber-700 text-xs w-28">Aktueller Code:</span>
                <span className="font-mono font-bold text-lg text-amber-800 tracking-widest">{userInfo.verification_code}</span>
                <span className="text-xs text-amber-600 ml-2">Gültig bis: {userInfo.verification_code_expires ? new Date(userInfo.verification_code_expires).toLocaleString('de-DE') : '?'}</span>
              </div>
            )}
          </div>
        )}
        {verifyResult && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${verifyResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid="verify-result">
            {verifyResult.success ? '✓' : '✗'} {verifyResult.message}
          </div>
        )}
      </div>
    </div>
  );
}


const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700', bidding: 'bg-yellow-100 text-yellow-700',
  booked: 'bg-emerald-100 text-emerald-700', completed: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-red-100 text-red-700',
};
const STATUS_LABELS = {
  open: 'Offen', bidding: 'Angebote', booked: 'Gebucht', completed: 'Abgeschlossen', cancelled: 'Storniert',
};
const DRIVER_STATUS = {
  pending: { label: 'Ausstehend', cls: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Aktiv', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700' },
  blocked: { label: 'Gesperrt', cls: 'bg-red-100 text-red-700' },
};
const DOC_LABELS = {
  selfie_with_license: 'Selfie mit Führerschein', vehicle_registration: 'Fahrzeugzulassung',
  vehicle_photo: 'Fahrzeugfoto', insurance: 'Versicherungsnachweis', transport_license: 'Transportlizenz',
};


function NotificationSettingsPanel() {
  const [settings, setSettings] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    api.get('/admin/notification-settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);
  const toggle = async (key) => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await api.put('/admin/notification-settings', { [key]: !settings[key] });
      setSettings(r.data);
    } catch {}
    finally { setSaving(false); }
  };
  if (!settings) return null;
  const channels = [
    { key: 'sms_new_transfer', label: 'SMS bei neuer Anfrage', icon: MessageSquare },
    { key: 'email_new_transfer', label: 'E-Mail bei neuer Anfrage', icon: Mail },
    { key: 'push_new_transfer', label: 'Push bei neuer Anfrage', icon: Settings },
    { key: 'sms_booking_confirmed', label: 'SMS bei Buchung', icon: MessageSquare },
    { key: 'email_booking_confirmed', label: 'E-Mail bei Buchung', icon: Mail },
    { key: 'email_document_expiry', label: 'Dokument-Ablauf Warnung', icon: Clock },
  ];
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-5 mt-6 col-span-full" data-testid="overview-notification-settings">
      <h2 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2"><Settings size={14} /> Benachrichtigungs-Kanäle</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        {channels.map(ch => {
          const Icon = ch.icon;
          const isOn = settings[ch.key] !== false;
          return (
            <div key={ch.key} className={`flex items-center justify-between p-2.5 rounded-lg border ${isOn ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center gap-2">
                <Icon size={12} className={isOn ? 'text-emerald-600' : 'text-slate-400'} />
                <span className="text-xs text-slate-700">{ch.label}</span>
              </div>
              <button onClick={() => toggle(ch.key)} disabled={saving}
                className={`w-10 h-5 rounded-full relative transition-colors ${isOn ? 'bg-emerald-500' : 'bg-slate-300'}`} data-testid={`overview-toggle-${ch.key}`}>
                <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform shadow ${isOn ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const { user, token, loadFromStorage, logout, language } = useAuthStore();
  const { t } = useTranslation(language);
  const navigate = useNavigate();
  const goToDriver = (id) => navigate(`/admin/driver/${id}`);
  const [stats, setStats] = useState(null);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [allTransfers, setAllTransfers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [transferFilter, setTransferFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [docViewerData, setDocViewerData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [emailForm, setEmailForm] = useState({ recipients: 'all_customers', subject: '', message: '', custom_emails: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [driverPayouts, setDriverPayouts] = useState([]);
  const [quickPayoutLoading, setQuickPayoutLoading] = useState(null);
  const [quickPayoutConfirm, setQuickPayoutConfirm] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  const profileMenuRef = useRef(null);

  useEffect(() => { loadFromStorage(); }, [loadFromStorage]);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (token && user?.role === 'admin') {
      loadData();
    } else if (!token) {
      setLoading(false);
    }
  }, [token, user]);

  // Retry if data didn't load after first attempt
  useEffect(() => {
    if (token && user?.role === 'admin' && !loading && !dataLoaded) {
      const retryTimer = setTimeout(() => {
        loadData();
      }, 1500);
      return () => clearTimeout(retryTimer);
    }
  }, [token, user, loading, dataLoaded]);

  const loadData = async () => {
    setLoading(true);
    let anySuccess = false;
    try {
      // Phase 1: Stats first (lightest query)
      try {
        const statsR = await api.get('/admin/stats');
        if (statsR.data) { setStats(statsR.data); anySuccess = true; }
      } catch (e) { console.error('[Admin] stats failed:', e.message); }

      // Show UI as soon as possible
      setLoading(false);
      setDataLoaded(true);

      // Phase 2: Overview + Drivers data
      try {
        const [pendingR, transfersR, driversR] = await Promise.all([
          api.get('/admin/pending-drivers').catch(() => ({ data: [] })),
          api.get('/admin/all-transfers').catch(() => ({ data: { transfers: [] } })),
          api.get('/admin/all-drivers', { params: { limit: 50, skip: 0 } }).catch(() => ({ data: { drivers: [] } })),
        ]);
        setPendingDrivers(pendingR.data || []);
        setAllTransfers(transfersR.data?.transfers || transfersR.data || []);
        setAllDrivers(driversR.data?.drivers || []);
        anySuccess = true;
      } catch (e) { console.error('[Admin] phase2:', e.message); }

      // Phase 3: Secondary data
      try {
        const [companiesR, usersR] = await Promise.all([
          api.get('/admin/all-companies').catch(() => ({ data: [] })),
          api.get('/admin/all-users').catch(() => ({ data: { users: [] } })),
        ]);
        setAllCompanies(companiesR.data || []);
        setAllUsers(usersR.data?.users || []);
      } catch (e) { console.error('[Admin] phase3:', e.message); }

      // Phase 4: Tertiary data
      try {
        const [paymentsR, analyticsR, emailLogR, payoutsR] = await Promise.all([
          api.get('/admin/payments').catch(() => ({ data: [] })),
          api.get('/admin/analytics').catch(() => ({ data: null })),
          api.get('/admin/email-log').catch(() => ({ data: [] })),
          api.get('/admin/driver-payouts').catch(() => ({ data: [] })),
        ]);
        setAllPayments(paymentsR.data || []);
        if (analyticsR.data) setAnalytics(analyticsR.data);
        setEmailLog(emailLogR.data || []);
        setDriverPayouts(payoutsR.data || []);
      } catch (e) { console.error('[Admin] phase4:', e.message); }

    } catch (err) {
      console.error('[Admin] loadData fatal:', err);
    } finally {
      setLoading(false);
      if (anySuccess) setDataLoaded(true);
    }
  };

  const resendEmail = async (transferId, type, recipient) => {
    const key = `${transferId}-${type}-${recipient || ''}`;
    setResendingEmail(key);
    try {
      if (type === 'booking') {
        await api.post(`/admin/transfer/${transferId}/resend-booking-email?recipient=${recipient}`);
        alert(`Buchungsbestätigung an ${recipient === 'customer' ? 'Kunde' : 'Fahrer'} wird gesendet!`);
      } else if (type === 'offer') {
        await api.post(`/admin/transfer/${transferId}/resend-offer-email`);
        alert('Angebots-E-Mail(s) an den Kunden werden gesendet!');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Senden der E-Mail');
    } finally {
      setResendingEmail(null);
    }
  };

  const handleAction = async (action, id, type = 'driver') => {
    const msgs = {
      approve: 'Fahrer genehmigen?', reject: 'Fahrer ablehnen?',
      block: `${type === 'company' ? 'Unternehmen' : 'Fahrer'} sperren?`,
      unblock: `${type === 'company' ? 'Unternehmen' : 'Fahrer'} entsperren?`,
    };
    if (!window.confirm(msgs[action])) return;
    try {
      const endpoint = type === 'company'
        ? `/admin/${action}-company/${id}`
        : `/admin/${action}-driver/${id}`;
      await api.post(endpoint);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleAdminProfileUpdated = useCallback((data) => {
    if (data.user) {
      loadFromStorage();
    }
  }, [loadFromStorage]);

  const viewDocuments = async (driverId) => {
    try {
      const res = await api.get(`/admin/driver/${driverId}/documents`);
      setDocViewerData(res.data);
    } catch (err) { alert('Dokumente konnten nicht geladen werden'); }
  };

  const handleExport = async (type) => {
    try {
      const res = await api.get(`/admin/export/${type}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch { alert('Export fehlgeschlagen'); }
  };

  const handleQuickPaypalPayout = async (dp) => {
    setQuickPayoutLoading(dp.driver_id);
    try {
      await api.post(`/admin/driver/${dp.driver_id}/payout`, {
        amount: dp.balance_owed, method: 'paypal', notes: 'Schnelle PayPal-Auszahlung', auto_paypal: true,
      });
      setQuickPayoutConfirm(null);
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'PayPal-Auszahlung fehlgeschlagen');
    } finally { setQuickPayoutLoading(null); }
  };

  const filteredDrivers = allDrivers.filter(d => {
    if (driverFilter !== 'all' && d.status !== driverFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return d.user?.name?.toLowerCase().includes(q) || d.user?.email?.toLowerCase().includes(q) || d.region?.toLowerCase().includes(q);
    }
    return true;
  });

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Admin-Zugang erforderlich</h2>
          <a href="/" className="text-orange-500 hover:underline">Zur Startseite</a>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Übersicht', icon: TrendingUp },
    { id: 'analytics', label: 'Analytik', icon: TrendingUp },
    { id: 'pending', label: `Freischaltung (${pendingDrivers.length})`, icon: Clock },
    { id: 'drivers', label: 'Fahrer', icon: Car },
    { id: 'companies', label: 'Unternehmen', icon: Building2 },
    { id: 'transfers', label: 'Transfers', icon: ClipboardList },
    { id: 'payouts', label: 'Auszahlungen', icon: Wallet },
    { id: 'payments', label: 'Zahlungen', icon: CreditCard },
    { id: 'users', label: 'Benutzer', icon: Users },
    { id: 'reviews', label: 'Bewertungen', icon: Star },
    { id: 'vehicle-cms', label: 'Fahrzeuge CMS', icon: Car },
    { id: 'notifications', label: 'Benachrichtigungen', icon: MessageSquare },
    { id: 'sms-stats', label: 'SMS-Statistik', icon: MessageSquare },
    { id: 'emails', label: 'E-Mails', icon: Mail },
    { id: 'settings', label: 'Einstellungen', icon: DollarSign },
    { id: 'maintenance', label: 'DB-Wartung', icon: Database },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <nav className="bg-white border-b border-slate-100 px-6 py-3 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-baseline gap-0.5">
              <span className="text-xl font-extrabold text-orange-500">366</span>
              <span className="text-xs font-semibold text-slate-800">Transfer</span>
            </a>
            <span className="text-sm font-bold text-slate-800">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleExport('transfers')} className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600" data-testid="export-transfers-btn">
              <Download size={14} /> Transfers
            </button>
            <button onClick={() => handleExport('drivers')} className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600" data-testid="export-drivers-btn">
              <Download size={14} /> Fahrer
            </button>
            {/* Admin Profile Menu */}
            <div className="relative ml-2" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full hover:bg-slate-200 transition-colors"
                data-testid="admin-profile-menu-btn"
              >
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-medium text-slate-700">{user?.name}</span>
                <ChevronDown size={12} className={`text-slate-500 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
              </button>
              {showProfileMenu && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-50" data-testid="admin-profile-dropdown">
                  <button
                    onClick={() => { setShowProfileMenu(false); setShowProfileSettings(true); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="admin-profile-edit-btn"
                  >
                    <Settings size={16} className="text-slate-400" />
                    Profil-Einstellungen
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  <button
                    onClick={() => { setShowProfileMenu(false); logout(); window.location.href = '/'; }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    data-testid="admin-logout-btn"
                  >
                    <LogOut size={16} className="text-red-400" />
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Refresh button when data didn't load */}
        {!loading && !stats && dataLoaded && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between" data-testid="data-reload-banner">
            <p className="text-sm text-amber-800">Daten konnten nicht geladen werden.</p>
            <button onClick={() => { setDataLoaded(false); loadData(); }}
              className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
              data-testid="reload-data-btn">
              <RefreshCw size={14} /> Erneut laden
            </button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { l: 'Benutzer', v: stats.total_users, icon: Users, c: 'text-blue-600 bg-blue-50' },
              { l: 'Fahrer', v: `${stats.approved_drivers}/${stats.total_drivers}`, icon: Car, c: 'text-green-600 bg-green-50' },
              { l: 'Ausstehend', v: stats.pending_drivers, icon: Clock, c: 'text-orange-600 bg-orange-50' },
              { l: 'Transfers', v: stats.total_transfers, icon: ClipboardList, c: 'text-purple-600 bg-purple-50' },
              { l: 'Bewertungen', v: stats.total_ratings, icon: Star, c: 'text-amber-600 bg-amber-50' },
              { l: 'Umsatz', v: `${stats.total_revenue}€`, icon: DollarSign, c: 'text-emerald-600 bg-emerald-50' },
            ].map(s => {
              const I = s.icon;
              return (
                <div key={s.l} className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className={`w-8 h-8 rounded-lg ${s.c} flex items-center justify-center mb-2`}><I size={16} /></div>
                  <p className="text-xl font-bold text-slate-800">{s.v}</p>
                  <p className="text-xs text-slate-400">{s.l}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1">
          {TABS.map(tab => {
            const I = tab.icon;
            return (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`} data-testid={`tab-${tab.id}`}>
                <I size={14} /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview */}
        {activeTab === 'overview' && loading && (
          <div className="flex items-center justify-center py-20" data-testid="overview-loading">
            <div className="text-center">
              <Loader2 size={32} className="animate-spin text-orange-500 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Daten werden geladen...</p>
            </div>
          </div>
        )}
        {activeTab === 'overview' && !loading && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-bold text-sm text-slate-800">Neueste Transfers</h2>
                <button onClick={() => setActiveTab('transfers')} className="text-xs text-orange-500">Alle</button>
              </div>
              <div className="divide-y divide-slate-50">
                {allTransfers.slice(0, 6).map(t => (
                  <div key={t.id} className="px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedTransfer(t)}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{t.customer_name}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{t.pickup_location?.address?.split(',')[0]} → {t.dropoff_location?.address?.split(',')[0]}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="font-bold text-sm text-slate-800">Ausstehende Freischaltungen</h2>
                  <span className="bg-orange-100 text-orange-600 px-2 py-0.5 rounded text-xs font-medium">{pendingDrivers.length}</span>
                </div>
                {pendingDrivers.length === 0 ? (
                  <div className="px-5 py-8 text-center text-slate-400 text-sm">Keine ausstehenden Bewerbungen</div>
                ) : pendingDrivers.slice(0, 4).map(d => {
                  const firstV = d.vehicles?.[0] || d.vehicle || {};
                  return (
                  <div key={d.id} className="px-5 py-3 border-b border-slate-50 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{d.user?.name}</p>
                      <p className="text-xs text-slate-400">{d.region} - {firstV.make} {firstV.model}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => goToDriver(d.id)} className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100" data-testid={`docs-${d.id}`}><Eye size={14} /></button>
                      <button onClick={() => handleAction('approve', d.id)} className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100" data-testid={`approve-${d.id}`}><CheckCircle size={14} /></button>
                      <button onClick={() => handleAction('reject', d.id)} className="p-1.5 bg-red-50 text-red-500 rounded-lg hover:bg-red-100" data-testid={`reject-${d.id}`}><XCircle size={14} /></button>
                    </div>
                  </div>
                  );
                })}
              </div>
              <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100">
                  <h2 className="font-bold text-sm text-slate-800">Unternehmen</h2>
                </div>
                {allCompanies.length === 0 ? (
                  <div className="px-5 py-8 text-center text-slate-400 text-sm">Keine Unternehmen registriert</div>
                ) : allCompanies.slice(0, 4).map(c => (
                  <div key={c.id} className="px-5 py-3 border-b border-slate-50 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{c.company_name}</p>
                      <p className="text-xs text-slate-400">{c.vehicle_count} Fahrzeuge - {c.driver_count} Fahrer</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {c.status === 'blocked' ? 'Gesperrt' : 'Aktiv'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notification Channel Settings */}
            <NotificationSettingsPanel />
          </div>
        )}

        {/* Pending Tab */}
        {activeTab === 'analytics' && (
          <AdminAnalytics analytics={analytics} />
        )}

        {/* Pending Approvals Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingDrivers.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400">
                <CheckCircle size={32} className="mx-auto mb-2 opacity-50" />
                <p>Keine ausstehenden Bewerbungen</p>
              </div>
            ) : pendingDrivers.map(d => {
              const pv = d.vehicles?.[0] || d.vehicle || {};
              return (
              <div key={d.id} className="bg-white rounded-xl border border-slate-100 p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-lg">
                        {d.user?.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">{d.user?.name}</p>
                        <p className="text-sm text-slate-500">{d.user?.email} | {d.user?.phone || '-'}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">Region: {d.region}</span>
                      <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">{pv.make} {pv.model} ({pv.year})</span>
                      <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">{pv.plate_number}</span>
                      <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">{pv.vehicle_type}</span>
                      {(d.vehicles?.length || 0) > 1 && <span className="px-2.5 py-1 bg-orange-50 rounded-lg text-xs text-orange-600">+{d.vehicles.length - 1} weitere Fahrzeuge</span>}
                      <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">Sprachen: {d.languages?.join(', ')}</span>
                    </div>
                    {/* Payment accounts */}
                    {(d.paypal_email || d.instapay_number || d.vodafone_cash_number) && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Wallet size={13} className="text-slate-400 mt-0.5" />
                        {d.paypal_email && <span className="px-2.5 py-1 bg-blue-50 rounded-lg text-xs text-blue-600" data-testid={`pending-paypal-${d.id}`}>PayPal: {d.paypal_email}</span>}
                        {d.instapay_number && <span className="px-2.5 py-1 bg-green-50 rounded-lg text-xs text-green-600" data-testid={`pending-instapay-${d.id}`}>InstaPay: {d.instapay_number}</span>}
                        {d.vodafone_cash_number && <span className="px-2.5 py-1 bg-red-50 rounded-lg text-xs text-red-600" data-testid={`pending-vodafone-${d.id}`}>Vodafone Cash: {d.vodafone_cash_number}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button onClick={() => goToDriver(d.id)} className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100" data-testid={`view-docs-${d.id}`}>
                      <FileText size={15} /> Details
                    </button>
                    <button onClick={() => handleAction('approve', d.id)} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-medium hover:bg-emerald-100" data-testid={`approve-pending-${d.id}`}>
                      <CheckCircle size={15} /> Genehmigen
                    </button>
                    <button onClick={() => handleAction('reject', d.id)} className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-500 rounded-lg text-sm font-medium hover:bg-red-100" data-testid={`reject-pending-${d.id}`}>
                      <XCircle size={15} /> Ablehnen
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <DriversTabContent
            allDrivers={allDrivers}
            setAllDrivers={setAllDrivers}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            driverFilter={driverFilter}
            setDriverFilter={setDriverFilter}
            goToDriver={goToDriver}
            handleAction={handleAction}
          />
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <div className="space-y-4">
            {allCompanies.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400">Keine Unternehmen registriert</div>
            ) : allCompanies.map(c => (
              <div key={c.id} className="bg-white rounded-xl border border-slate-100 p-5 cursor-pointer hover:border-orange-200 transition-colors" onClick={() => setSelectedCompany(c.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center text-white font-bold">
                      <Building2 size={20} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">{c.company_name}</p>
                      <p className="text-sm text-slate-500">{c.owner?.name} | {c.owner?.email}</p>
                      <div className="flex gap-2 mt-2">
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">{c.region}</span>
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">{c.vehicle_count} Fahrzeuge</span>
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">{c.driver_count} Fahrer</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {c.status === 'blocked' ? 'Gesperrt' : 'Aktiv'}
                    </span>
                    <button onClick={() => setSelectedCompany(c.id)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100" data-testid={`view-company-${c.id}`}>
                      <Eye size={14} /> Details
                    </button>
                    {c.status !== 'blocked' ? (
                      <button onClick={() => handleAction('block', c.id, 'company')} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-500 rounded-lg text-sm hover:bg-red-100" data-testid={`block-company-${c.id}`}>
                        <Ban size={14} /> Sperren
                      </button>
                    ) : (
                      <button onClick={() => handleAction('unblock', c.id, 'company')} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-sm hover:bg-emerald-100" data-testid={`unblock-company-${c.id}`}>
                        <Unlock size={14} /> Entsperren
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Transfers Tab */}
        {activeTab === 'transfers' && (
          <AdminTransfersTab
            transfers={allTransfers}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            transferFilter={transferFilter}
            setTransferFilter={setTransferFilter}
            onSelectTransfer={setSelectedTransfer}
            resendEmail={resendEmail}
            resendingEmail={resendingEmail}
          />
        )}

        {/* Driver Payouts Tab */}
        {activeTab === 'payouts' && (
          <AdminPayoutsTab
            driverPayouts={driverPayouts}
            goToDriver={goToDriver}
            quickPayoutConfirm={quickPayoutConfirm}
            setQuickPayoutConfirm={setQuickPayoutConfirm}
            quickPayoutLoading={quickPayoutLoading}
            handleQuickPaypalPayout={handleQuickPaypalPayout}
          />
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <h2 className="font-bold text-sm text-slate-800">Zahlungsübersicht ({allPayments.length} Transaktionen)</h2>
            </div>
            {allPayments.length === 0 ? (
              <div className="p-12 text-center text-slate-400">Keine Zahlungen</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Kunde</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Betrag</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Methode</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Datum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {allPayments.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="px-5 py-3"><p className="text-sm text-slate-800">{p.customer_name || '-'}</p><p className="text-xs text-slate-400">{p.customer_email || ''}</p></td>
                        <td className="px-5 py-3 font-semibold text-sm text-slate-800">{p.amount} {(p.currency || 'EUR').toUpperCase()}</td>
                        <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${p.payment_method === 'paypal' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{p.payment_method === 'paypal' ? 'PayPal' : 'Stripe'}</span></td>
                        <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${p.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>{p.payment_status === 'paid' ? 'Bezahlt' : p.payment_status}</span></td>
                        <td className="px-5 py-3 text-sm text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('de-DE') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <UsersTab
            users={allUsers}
            onRefresh={loadData}
          />
        )}

        {activeTab === 'vehicle-cms' && (
          <VehicleCategoryCMS />
        )}

        {activeTab === 'reviews' && (
          <AdminReviewsTab />
        )}

        {activeTab === 'notifications' && (
          <AdminNotificationsTab />
        )}

        {activeTab === 'sms-stats' && (
          <AdminSmsStatsTab />
        )}

        {activeTab === 'maintenance' && (
          <AdminMaintenanceTab />
        )}

        {activeTab === 'settings' && (
          <AdminSettingsTab />
        )}

        {activeTab === 'emails' && (
          <div className="space-y-6">
            {/* Email Diagnostics */}
            <EmailDiagnostics />
            {/* Send Email Form */}
            <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="admin-email-form">
              <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Mail size={18} className="text-orange-500" /> E-Mail senden
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Empfänger</label>
                  <select
                    value={emailForm.recipients}
                    onChange={e => setEmailForm(f => ({ ...f, recipients: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm bg-white"
                    data-testid="email-recipients-select"
                  >
                    <option value="all_customers">Alle Kunden</option>
                    <option value="all_drivers">Alle Fahrer</option>
                    <option value="all_users">Alle Benutzer</option>
                    <option value="custom">Benutzerdefiniert</option>
                  </select>
                </div>
                {emailForm.recipients === 'custom' && (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">E-Mail-Adressen (kommagetrennt)</label>
                    <input
                      type="text"
                      value={emailForm.custom_emails}
                      onChange={e => setEmailForm(f => ({ ...f, custom_emails: e.target.value }))}
                      placeholder="email1@example.com, email2@example.com"
                      className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                      data-testid="email-custom-input"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Betreff</label>
                  <input
                    type="text"
                    value={emailForm.subject}
                    onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="E-Mail Betreff"
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
                    data-testid="email-subject-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Nachricht</label>
                  <textarea
                    value={emailForm.message}
                    onChange={e => setEmailForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Ihre Nachricht..."
                    rows={6}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm resize-none"
                    data-testid="email-message-textarea"
                  />
                </div>
                <button
                  onClick={async () => {
                    if (!emailForm.subject || !emailForm.message) return;
                    setEmailSending(true);
                    try {
                      const payload = {
                        recipients: emailForm.recipients,
                        subject: emailForm.subject,
                        message: emailForm.message,
                      };
                      if (emailForm.recipients === 'custom') {
                        payload.custom_emails = emailForm.custom_emails.split(',').map(e => e.trim()).filter(Boolean);
                      }
                      const res = await api.post('/admin/send-email', payload);
                      alert(res.data.message);
                      setEmailForm({ recipients: 'all_customers', subject: '', message: '', custom_emails: '' });
                      const logRes = await api.get('/admin/email-log');
                      setEmailLog(logRes.data || []);
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Fehler beim Senden');
                    } finally { setEmailSending(false); }
                  }}
                  disabled={emailSending || !emailForm.subject || !emailForm.message}
                  className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                  data-testid="email-send-btn"
                >
                  {emailSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  E-Mail senden
                </button>
              </div>
            </div>

            {/* Email Log */}
            <div className="bg-white rounded-xl border border-slate-100" data-testid="admin-email-log">
              <div className="px-5 py-3 border-b border-slate-100">
                <h2 className="font-bold text-sm text-slate-800">Gesendete E-Mails ({emailLog.length})</h2>
              </div>
              {emailLog.length === 0 ? (
                <p className="p-6 text-sm text-slate-400 text-center">Noch keine E-Mails gesendet</p>
              ) : (
                <div className="divide-y divide-slate-50">
                  {emailLog.map(log => (
                    <div key={log.id} className="px-5 py-3 hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{log.subject}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {log.recipients_group === 'all_customers' ? 'Alle Kunden' :
                             log.recipients_group === 'all_drivers' ? 'Alle Fahrer' :
                             log.recipients_group === 'all_users' ? 'Alle Benutzer' : 'Benutzerdefiniert'}
                            {' '} - {log.recipients_count} Empfänger
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400">{log.created_at ? new Date(log.created_at).toLocaleString('de-DE') : '-'}</p>
                          <p className="text-xs text-slate-500">von {log.sent_by_name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Transfer Detail Modal */}
      {selectedTransfer && (
        <TransferDetailModal
          transfer={selectedTransfer}
          onClose={() => setSelectedTransfer(null)}
          resendEmail={resendEmail}
          resendingEmail={resendingEmail}
          onRefresh={loadData}
        />
      )}

      {/* Document Viewer Modal */}
      {docViewerData && (
        <DocumentViewerModal
          data={docViewerData}
          onClose={() => setDocViewerData(null)}
          onAction={(action, id) => { handleAction(action, id); setDocViewerData(null); }}
        />
      )}

      {/* Profile Settings Modal */}
      {showProfileSettings && (
        <ProfileSettingsModal
          t={t}
          driverProfile={null}
          onClose={() => setShowProfileSettings(false)}
          onProfileUpdated={handleAdminProfileUpdated}
        />
      )}

      {/* Company Detail Modal */}
      {selectedCompany && (
        <CompanyDetailModal
          companyId={selectedCompany}
          onClose={() => setSelectedCompany(null)}
          onAction={(action, id, type) => { handleAction(action, id, type); setSelectedCompany(null); }}
          onRefresh={loadData}
        />
      )}
    </div>
  );
}
